import { MouseEvent } from 'react'
import { Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CorneredSection } from '@ui2/structure'
import { IfcTypeItem } from '../Controls/IFCUploadWidget/components/IFCFileConfig/IfcTypeItem'
import {
  ifcElementTypeLabelsOrder,
  ifcFilterTypeLabels,
} from '../Controls/IFCUploadWidget/constants/ifcFilter'

interface IFCTypeFilterProps {
  importedTypes: string[]
  notImportedTypes: string[]
  glbTypeVisibility: Record<string, boolean | undefined>
  onToggleTypeVisibility: (type: string, visible: boolean) => void
  onSelectType?: (type: string, event: MouseEvent<HTMLButtonElement>) => void
}

const IFCTypeFilter = ({
  importedTypes,
  notImportedTypes,
  glbTypeVisibility,
  onToggleTypeVisibility,
  onSelectType,
}: IFCTypeFilterProps) => {
  const theme = useTheme()
  const typeColors = theme.scenePalette.ifc as Record<string, string>
  const orderMap = new Map<string, number>(
    ifcElementTypeLabelsOrder.map((type, index) => [type, index]),
  )

  const sortTypesByConfiguredOrder = (types: string[]) =>
    [...types].sort((a, b) => {
      const aOrder = orderMap.get(a) ?? Number.MAX_SAFE_INTEGER
      const bOrder = orderMap.get(b) ?? Number.MAX_SAFE_INTEGER

      if (aOrder !== bOrder) return aOrder - bOrder

      const aLabel = ifcFilterTypeLabels[a] ?? a
      const bLabel = ifcFilterTypeLabels[b] ?? b
      return aLabel.localeCompare(bLabel, 'de')
    })

  const sortedImportedTypes = sortTypesByConfiguredOrder(importedTypes)
  const sortedNotImportedTypes = sortTypesByConfiguredOrder(notImportedTypes)

  if (importedTypes.length === 0 && notImportedTypes.length === 0) return null

  return (
    <Stack spacing={2}>
      {importedTypes.length > 0 && (
        <CorneredSection title="Importierte IFC-Typen" collapsible>
          {sortedImportedTypes.map(type => (
            <IfcTypeItem
              key={type}
              title={ifcFilterTypeLabels[type] ?? type}
              visible={glbTypeVisibility[type] !== false}
              bgColor={typeColors[type]}
              onToggleVisibility={() =>
                onToggleTypeVisibility(type, glbTypeVisibility[type] === false)
              }
              onClickSelect={onSelectType ? event => onSelectType(type, event) : undefined}
            />
          ))}
        </CorneredSection>
      )}

      {notImportedTypes.length > 0 && (
        <CorneredSection title="Weitere IFC-Typen" collapsible defaultOpen={false}>
          {sortedNotImportedTypes.map(type => (
            <IfcTypeItem
              key={type}
              title={ifcFilterTypeLabels[type] ?? type}
              visible={glbTypeVisibility[type] !== false}
              bgColor={typeColors[type]}
              onToggleVisibility={() =>
                onToggleTypeVisibility(type, glbTypeVisibility[type] === false)
              }
              onClickSelect={onSelectType ? event => onSelectType(type, event) : undefined}
            />
          ))}
        </CorneredSection>
      )}
    </Stack>
  )
}

export default IFCTypeFilter
